import type { ReactNode } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { Hint } from './Hint';
import { Label } from './Label';
import { cx, describedBy } from './cx';

export interface SwitchProps {
  id: string;
  name?: string;
  label: ReactNode;
  labelLang?: string;
  labelClassName?: string;
  hint?: ReactNode;
  hintLang?: string;
  error?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Visible state text next to the switch ("Zapnuté" / "Vypnuté"). */
  onText: string;
  offText: string;
  /** Unavailable: stays focusable and announced (aria-disabled), changes are blocked. */
  ariaDisabled?: boolean;
  beforeInput?: ReactNode;
  extraDescribedBy?: string;
  formGroupClassName?: string;
}

/**
 * On/off switch (ID-SK has none): a native checkbox with `role="switch"`, so screen readers announce "on/off" and
 * Space toggles it, drawn as a track with a thumb. The state is also shown as text, never by colour alone.
 */
export function Switch({
  id,
  name,
  label,
  labelLang,
  labelClassName,
  hint,
  hintLang,
  error,
  checked,
  onChange,
  onText,
  offText,
  ariaDisabled,
  beforeInput,
  extraDescribedBy,
  formGroupClassName,
}: SwitchProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <Label htmlFor={id} className={labelClassName} lang={labelLang}>
        {label}
      </Label>
      {hint && (
        <Hint id={hintId} lang={hintLang}>
          {hint}
        </Hint>
      )}
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      {beforeInput}
      <span className={cx('app-switch', checked && 'app-switch--on', ariaDisabled && 'app-switch--disabled')}>
        <input
          id={id}
          name={name}
          className="app-switch__input"
          type="checkbox"
          role="switch"
          checked={checked}
          aria-disabled={ariaDisabled ? true : undefined}
          aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            if (!ariaDisabled) onChange(e.target.checked);
          }}
        />
        <span className="app-switch__track" aria-hidden="true">
          <span className="app-switch__thumb" />
        </span>
        <span className="app-switch__state" aria-hidden="true">
          {checked ? onText : offText}
        </span>
      </span>
    </div>
  );
}
